/**
 * Component that listens for new leads via socket and shows notifications
 * Also triggers data refetch when new lead arrives
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { UserPlus } from 'lucide-react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useNewLeadSocket, NewLeadData } from '../../hooks/useNewLeadSocket'

interface NewLeadNotificationProps {
    enabled?: boolean
}

const NewLeadNotification: React.FC<NewLeadNotificationProps> = ({
    enabled = true,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const handleNewLead = useCallback(
        (data: NewLeadData) => {
            // Show toast notification
            toast.custom(
                (toastInstance) => (
                    <div
                        className={`${
                            toastInstance.visible
                                ? 'animate-enter'
                                : 'animate-leave'
                        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
                    >
                        <div className="flex-1 w-0 p-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 pt-0.5">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <UserPlus
                                            size={20}
                                            className="text-primary"
                                        />
                                    </div>
                                </div>
                                <div className="ml-3 flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                        {t('new-lead-received')}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {data.contact_source_name}
                                        {data.contact_source_company &&
                                            ` - ${data.contact_source_company}`}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {data.contact_source_channel} •{' '}
                                        {data.website_referrer}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex border-l border-gray-200">
                            <button
                                onClick={() => toast.dismiss(toastInstance.id)}
                                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary hover:text-primary/80 focus:outline-none"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                ),
                {
                    duration: 8000,
                    position: 'top-right',
                }
            )

            // Invalidate queries to refetch data
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MARKETING],
            })
        },
        [queryClient, t]
    )

    // Connect socket and listen for new leads
    useNewLeadSocket({
        onNewLead: handleNewLead,
        enabled,
    })

    // This component doesn't render anything visible
    return null
}

export default NewLeadNotification
